import "../../css/ProfilePage/ProfilePage.css";
import { memo, useEffect, useMemo, useState } from "react";
import {
  ProfileDisplayView,
  ProfilePageTimelineHeader,
  ProfileTimelineState,
} from "./ProfilePageTimelineHeader";
import ProfilePageTimeline from "./ProfilePageTimeline";
import { useOnMount } from "../../hooks/useOnMount";
import { useSearchParams } from "react-router-dom";
import {
  datesAreOnSameDay,
  foundUserGenericTrackingLinks,
  foundUserLinkForms,
  hostedEvents,
  hostingLiveEvents,
  isProductNonPostType,
  LessThanDate,
} from "@markit/common.utils";
import {
  AccountData,
  Event,
  EventType,
  ProfilePageSection,
  Visibility,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState, LoginState } from "../../redux/slices/accountSlice";
import { isCreatorBasedAccount } from "../../utils/userUtils";

type ProfilePageTimelineSectionProps = {
  userData: AccountData;
  profilePageSection: ProfilePageSection;
  eventOrganizers: AccountData[];
  userEvents: Event[];
  userAttendingEvents: Event[];
  loadingEvents: boolean;
};

const ProfilePageTimelineSection = memo(function ProfilePageTimelineSectionFn(
  props: ProfilePageTimelineSectionProps
) {
  const {
    userData,
    profilePageSection,
    eventOrganizers,
    userEvents,
    userAttendingEvents,
    loadingEvents,
  } = props;
  const { loggedIn, accountData } = useSelector(getAccountState).account;
  const [searchParams] = useSearchParams();
  const [eventTimelineState, setEventTimelineState] =
    useState<ProfileTimelineState>(ProfileTimelineState.UPCOMING);
  const [eventDisplayView, setEventDisplayView] = useState<ProfileDisplayView>(
    ProfileDisplayView.CARD_VIEW
  );
  const [eventDates, setEventDates] = useState<string[]>([]);
  const [showMoreEvents, setShowMoreEvents] = useState(false);
  const [loadingTimelineState, setLoadingTimelineState] = useState(true);

  useOnMount(() => {
    // If condensed is part of the url, show the grid view defaulted
    const condensedView = searchParams.get("condensed");
    if (condensedView === "true") {
      setEventDisplayView(ProfileDisplayView.GRID_VIEW);
    }
  });

  const isMyAccount = useMemo(
    () =>
      loggedIn === LoginState.LOGGED_IN && accountData.uid === userData?.uid,
    [accountData.uid, loggedIn, userData?.uid]
  );

  const userForms = useMemo(() => {
    const forms = foundUserLinkForms(userEvents, userData.uid, "active");
    return forms;
  }, [userData.uid, userEvents]);

  const userTrackingLinks = useMemo(() => {
    const links = foundUserGenericTrackingLinks(
      userEvents,
      userData.uid,
      "active"
    );
    return links;
  }, [userData.uid, userEvents]);

  const userPosts = useMemo(
    () =>
      userEvents
        .filter(
          (post) => post.eventType === EventType.POST && !post.isManualSoldOut
        )
        .reverse(),
    [userEvents]
  );

  // Products (excluding posts)
  const userProducts = useMemo(() => {
    return userEvents
      .filter(
        (product) =>
          isProductNonPostType(product.eventType) && !product.isManualSoldOut
      )
      .reverse();
  }, [userEvents]);

  // TODO (jonathan) 7/5/26: Uses the old logic here since we don't have the users upcoming tickets available here. So when we choose to deprecate the organizer arrays on the event objects, we'll have to change this logic
  const upcomingEvents = useMemo(() => {
    const hosting = hostingLiveEvents(userEvents, userData.uid, true);
    return hosting;
  }, [userData.uid, userEvents]);

  const pastEvents = useMemo(() => {
    const hosted = hostedEvents(userEvents, userData.uid);
    return hosted.reverse();
  }, [userData.uid, userEvents]);

  const attendingEvents = useMemo(() => {
    const attending = userAttendingEvents.filter((event) =>
      LessThanDate(new Date().toISOString(), event.end)
    );
    return attending;
  }, [userAttendingEvents]);

  const attendedEvents = useMemo(() => {
    const attended = userAttendingEvents.filter((event) =>
      LessThanDate(event.end, new Date().toISOString())
    );
    return attended.reverse();
  }, [userAttendingEvents]);

  const eventsToShow = useMemo(() => {
    switch (eventTimelineState) {
      case ProfileTimelineState.UPCOMING:
        return upcomingEvents;
      case ProfileTimelineState.PAST:
        return pastEvents;
      case ProfileTimelineState.ATTENDING:
        return attendingEvents;
      case ProfileTimelineState.ATTENDED:
        return attendedEvents;
      default:
        return [];
    }
  }, [
    attendedEvents,
    attendingEvents,
    eventTimelineState,
    pastEvents,
    upcomingEvents,
  ]);

  // Only shows public items on timeline sections
  const userItemsToShow = useMemo(
    () =>
      (profilePageSection === ProfilePageSection.EVENTS_CALENDAR
        ? eventsToShow
        : profilePageSection === ProfilePageSection.POSTS_SECTION
          ? userPosts
          : profilePageSection === ProfilePageSection.PRODUCTS_SECTION
            ? userProducts
            : profilePageSection === ProfilePageSection.FORMS_SECTION
              ? userForms
              : profilePageSection === ProfilePageSection.LINKS_SECTION
                ? userTrackingLinks
                : []
      ).filter(
        (item) =>
          (isMyAccount && !accountData.inCreatorMode) ||
          item.visibility === Visibility.Public
      ),
    [
      accountData.inCreatorMode,
      eventsToShow,
      isMyAccount,
      profilePageSection,
      userForms,
      userPosts,
      userProducts,
      userTrackingLinks,
    ]
  );

  const showAllEvents = useMemo(
    () => showMoreEvents || eventDisplayView === ProfileDisplayView.GRID_VIEW,
    [eventDisplayView, showMoreEvents]
  );

  // Load the default timeline state, which is determined after loading all the events
  useEffect(() => {
    if (!loadingEvents && loadingTimelineState) {
      if (isMyAccount && !isCreatorBasedAccount(accountData)) {
        // Only load attending related states if user is viewing own account and is a subscriber
        setEventTimelineState(
          attendingEvents.length === 0
            ? ProfileTimelineState.ATTENDED
            : ProfileTimelineState.ATTENDING
        );
      } else {
        setEventTimelineState(
          upcomingEvents.length === 0
            ? pastEvents.length === 0
              ? attendingEvents.length === 0
                ? attendedEvents.length === 0
                  ? ProfileTimelineState.UPCOMING
                  : ProfileTimelineState.ATTENDED
                : ProfileTimelineState.ATTENDING
              : ProfileTimelineState.PAST
            : ProfileTimelineState.UPCOMING
        );
      }
      setLoadingTimelineState(false);
    }
  }, [
    accountData,
    accountData.inCreatorMode,
    accountData.phoneNumber,
    attendedEvents.length,
    attendingEvents.length,
    isMyAccount,
    loadingEvents,
    loadingTimelineState,
    pastEvents.length,
    setEventTimelineState,
    setLoadingTimelineState,
    upcomingEvents.length,
  ]);

  // Load the unique event dates for the events loaded in for the timeline
  useEffect(() => {
    if (profilePageSection === ProfilePageSection.EVENTS_CALENDAR) {
      const finalEvents = showAllEvents
        ? userItemsToShow
        : userItemsToShow.slice(0, 3);
      if (!loadingEvents) {
        if (finalEvents.length > 0) {
          const foundEventDates: string[] = [];
          for (let i = 0; i < finalEvents.length; i++) {
            if (
              !foundEventDates.some((eventDate) =>
                datesAreOnSameDay(
                  new Date(eventDate),
                  new Date(finalEvents[i].start)
                )
              )
            ) {
              foundEventDates.push(finalEvents[i].start);
            }
          }
          setEventDates(foundEventDates);
        }
      }
    }
  }, [
    profilePageSection,
    userItemsToShow,
    loadingEvents,
    setEventDates,
    showAllEvents,
    showMoreEvents,
  ]);

  return (
    <div className="ColumnNormal" style={{ gap: 14 }}>
      <ProfilePageTimelineHeader
        timelineState={eventTimelineState}
        setTimelineState={setEventTimelineState}
        displayView={eventDisplayView}
        setDisplayView={setEventDisplayView}
        profilePageSection={profilePageSection}
        isMyAccount={isMyAccount}
      />
      <ProfilePageTimeline
        displayView={eventDisplayView}
        timelineState={eventTimelineState}
        userItemsToShow={userItemsToShow}
        userData={userData}
        eventDates={eventDates}
        eventOrganizers={eventOrganizers}
        showMoreEvents={showMoreEvents}
        setShowMoreEvents={setShowMoreEvents}
        profilePageSection={profilePageSection}
      />
    </div>
  );
});

export default ProfilePageTimelineSection;
